import React from 'react';
import { Link } from 'react-router-dom';
import { Droplets, Gem, Sparkles, BedDouble, PanelTop, Fan, Sun, Shirt, PawPrint, AppWindow, Utensils, Trash2, PartyPopper, Package, HardHat } from 'lucide-react';
import './ServicesPage.css';

const ServicesPage = () => {
  const services = [
    {
      key: 'regular',
      name: 'Regular Cleaning',
      icon: Droplets,
      price: 'From $200',
      description: 'Easy, everyday cleaning to keep your home feeling fresh. We wipe surfaces, clean bathrooms, care for floors, tidy the kitchen, and take out the trash. Priced by home size for total transparency.',
      includes: ['Surface wiping', 'Bathroom cleaning', 'Floor care', 'Kitchen tidying', 'Trash removal'],
      ideal: 'Regular upkeep, maintenance cleaning',
    },
    {
      key: 'deep',
      name: 'Deep Cleaning',
      icon: Gem,
      price: 'From $275',
      description: 'When your home needs some extra love. We go deeper — baseboards, fixtures, and the often-overlooked details that make everything feel brand new again.',
      includes: ['Everything in Regular Cleaning', 'Baseboard cleaning', 'Fixture detailing', 'Detail work in overlooked areas', 'Like-new refined finish'],
      ideal: 'Spring cleaning, seasonal refresh, special occasions',
    },
    {
      key: 'maid_standard',
      name: 'Standard Maid Service',
      icon: Sparkles,
      price: '$220',
      description: 'Your regular keep-it-clean service at one flat rate. We tidy common areas, bathrooms, kitchen surfaces, floors, and trash. Beds are always made and looking neat — no linen change.',
      includes: ['Common area tidying', 'Bathroom cleaning', 'Kitchen surfaces', 'Floor care', 'Trash removal', 'Bed making'],
      ideal: 'Busy professionals, short-term rentals, regular maintenance',
    },
    {
      key: 'maid_linen',
      name: 'Maid Service & Linen Change',
      icon: BedDouble,
      price: '$280',
      description: 'Like a hotel refresh without checking out. Full maid service plus fresh sheets and towels, and perfectly made beds. Add personal laundry for $100.',
      includes: ['All standard maid service', 'Fresh sheet & towel change', 'Hotel-quality bed making', 'Personal laundry available (+$100)'],
      ideal: 'Guest turnovers, home comfort seekers, weekly refreshes',
    },
  ];

  const addOns = [
    { icon: PanelTop, name: 'Blinds Cleaning', price: '$25' },
    { icon: Fan, name: 'Ceiling Fans', price: '$20' },
    { icon: Sun, name: 'Balcony / Terrace', price: '$50' },
    { icon: Shirt, name: 'Personal Laundry', price: '$100' },
    { icon: PawPrint, name: 'Pet Hair Removal', price: 'from $45' },
    { icon: AppWindow, name: 'Interior Windows', price: '$10 / window' },
    { icon: Utensils, name: 'Dishwasher Interior', price: '$20' },
    { icon: Trash2, name: 'Trash Can Sanitizing', price: '$15' },
    { icon: PartyPopper, name: 'Post-Party Clean-Up', price: 'from $75' },
    { icon: Package, name: 'Move-In / Move-Out', price: 'from $100' },
  ];

  return (
    <div className="services-page">
      <section className="services-hero">
        <div className="container">
          <h1 className="page-title font-heading">Our Services</h1>
          <p className="page-subtitle">Professional cleaning tailored to your needs. Transparent pricing, guaranteed results.</p>
        </div>
      </section>

      <section className="services-content">
        <div className="container">
          <div className="services-list">
            {services.map((service, idx) => (
              <div key={idx} className="service-detail-card">
                <div className="service-header">
                  <div className="service-icon-large"><service.icon size={28} strokeWidth={1.5} /></div>
                  <div className="service-title-section">
                    <h2 className="service-title">{service.name}</h2>
                    <div className="service-price">{service.price}</div>
                  </div>
                </div>
                <p className="service-description">{service.description}</p>
                <div className="service-details">
                  <div className="service-includes">
                    <h4>What's Included:</h4>
                    <ul>
                      {service.includes.map((item, i) => (
                        <li key={i}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="service-ideal">
                    <strong>Ideal for:</strong> {service.ideal}
                  </div>
                </div>
                <Link
                  to={`/book?service=${service.key}`}
                  className="btn btn-primary service-book-btn"
                  data-testid={`services-book-${service.key}`}
                >
                  Book {service.name}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </Link>
              </div>
            ))}
          </div>

          <div className="addons-section">
            <h2 className="addons-title font-heading">Popular Add-Ons</h2>
            <p className="addons-subtitle">Customize any cleaning with these extras</p>
            <div className="addons-price-grid">
              {addOns.map((addon, idx) => (
                <div key={idx} className="addon-price-card">
                  <div className="addon-price-icon"><addon.icon size={20} strokeWidth={1.5} /></div>
                  <span className="addon-price-name">{addon.name}</span>
                  <span className="addon-price-value">{addon.price}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="specialty-note">
            <div className="specialty-note-icon"><HardHat size={24} strokeWidth={1.5} /></div>
            <div>
              <h3>Need something more specialized?</h3>
              <p>
                We also offer post-construction and post-event cleaning, customized based on
                scope and level of detail required. <Link to="/contact">Contact us</Link> for a custom quote.
              </p>
            </div>
          </div>

          <div className="services-cta">
            <h2 className="cta-heading font-heading">Ready to book?</h2>
            <p className="cta-text">Get your exact price in minutes — pay securely online</p>
            <Link to="/book" className="btn btn-primary btn-large" data-testid="services-book-btn">
              Book Your Cleaning Now
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
